import { useRef } from 'react';
import { Dex360Audit } from '@/api/dex360-audit';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { Updater } from '@tanstack/react-query';
import {
	flexRender,
	getCoreRowModel,
	getPaginationRowModel,
	PaginationState,
	SortingState,
	useReactTable,
} from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';
import { TablePagination } from '@/components/ui/custom/table-pagination';
import { getColumns } from './columns';

type Props = {
	data: Dex360Audit[];
	totalCount: number;
	pagination: PaginationState;
	setPagination: React.Dispatch<React.SetStateAction<PaginationState>>;
	sorting: SortingState;
	setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
	isActiveTab: boolean;
	onSingleHide?: (id: number) => void;
	rowSelection: Record<string, boolean>;
	setRowSelection: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
	isFetching: boolean;
};

export function Dex360AuditTable({
	data,
	totalCount,
	pagination,
	setPagination,
	sorting,
	setSorting,
	isActiveTab = false,
	isFetching = false,
	onSingleHide,
	rowSelection,
	setRowSelection,
}: Props) {
	const tableRef = useRef<HTMLTableElement>(null);

	const handlePageChange = (
		updater: Updater<PaginationState, PaginationState>,
	) => {
		setPagination((prev) => {
			const newPagination =
				typeof updater === 'function' ? updater(prev) : updater;

			return newPagination; // Allow page change
		});
	};

	const table = useReactTable({
		data,
		columns: getColumns(onSingleHide, isActiveTab, sorting, setSorting),
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		enableRowSelection: true,
		onRowSelectionChange: setRowSelection,
		getRowId: (row) => String(row.id),
		rowCount: totalCount,
		pageCount: Math.ceil(totalCount / pagination.pageSize),
		onPaginationChange: handlePageChange,
		manualPagination: true,
		manualSorting: true,
		onSortingChange: setSorting,
		state: {
			rowSelection,
			pagination,
			sorting,
		},
	});

	return (
		<>
			<div className="h-1/2 w-full">
				<div className="table-container relative rounded-md border">
					{isFetching && (
						<div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
							<Loader2 className="h-6 w-6 animate-spin text-gray-600" />
						</div>
					)}
					<div className="rounded-md border">
						<Table ref={tableRef}>
							<TableHeader>
								{table.getHeaderGroups().map((headerGroup) => (
									<TableRow key={headerGroup.id}>
										{headerGroup.headers.map((header) => (
											<TableHead key={header.id}>
												{header.isPlaceholder
													? null
													: flexRender(header.column.columnDef.header, header.getContext())}
											</TableHead>
										))}
									</TableRow>
								))}
							</TableHeader>
							<TableBody>
								{table.getRowModel().rows.length ? (
									table.getRowModel().rows.map((row) => (
										<TableRow key={row.id}>
											{row.getVisibleCells().map((cell) => (
												<TableCell key={cell.id}>
													{flexRender(cell.column.columnDef.cell, cell.getContext())}
												</TableCell>
											))}
										</TableRow>
									))
								) : (
									<TableRow>
										<TableCell
											colSpan={table.getAllColumns().length}
											className="py-10 text-center"
										>
											No results.
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</div>
				</div>

				{/* Footer */}
				<TablePagination table={table} isFetching={isFetching} />
			</div>
		</>
	);
}
